import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    TextField,
    Fade,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ReplayIcon from '@mui/icons-material/Replay';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { alpha, keyframes } from '@mui/system';

const cyan = '#00BFFF';
const green = '#8DC63F';
const yellow = '#FFD54F';
const darkBg = '#060B14';
const cardBg = '#0E1626';
const cardBorder = 'rgba(0, 191, 255, 0.15)';

const guideDotMove = keyframes`
    0% { left: 8%; }
    100% { left: 88%; }
`;

const glowPulse = keyframes`
    0%, 100% { opacity: 0.4; box-shadow: 0 0 8px 2px ${alpha(cyan, 0.6)}; }
    50% { opacity: 1; box-shadow: 0 0 20px 6px ${alpha(cyan, 0.9)}; }
`;

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
`;

const kitItems = [
    {
        icon: <AssignmentIcon sx={{ fontSize: 28, color: cyan }} />,
        title: '7-Day Reading Reset Worksheet',
        desc: 'A simple day-by-day plan for training speed, focus, comprehension, and retention.',
    },
    {
        icon: <SpeedIcon sx={{ fontSize: 28, color: cyan }} />,
        title: 'Reading Friction Audit',
        desc: 'Identify the habits that slow you down most, including rereading, visual drifting, lack of purpose, and reading every word with equal weight.',
    },
    {
        icon: <ReplayIcon sx={{ fontSize: 28, color: cyan }} />,
        title: 'Rereading Loop Tracker',
        desc: 'Notice when your eyes keep pulling backward and practice moving forward with confidence.',
    },
    {
        icon: <TimerIcon sx={{ fontSize: 28, color: cyan }} />,
        title: '15-Minute Routine Checklist',
        desc: 'A short daily routine for warming up, guided reading, controlled speed, comprehension checks, and retention notes.',
    },
    {
        icon: <LockIcon sx={{ fontSize: 28, color: cyan }} />,
        title: '3-2-1 Retention Template',
        desc: 'Capture 3 key ideas, 2 useful examples, and 1 action or question from what you read.',
    },
    {
        icon: <RadioButtonCheckedIcon sx={{ fontSize: 28, color: cyan }} />,
        title: 'Guided Session Tracker',
        desc: 'Track your ReaderFlex practice sessions, speed comfort, focus, comprehension, and retention.',
    },
];

const audienceBullets = [
    'You keep rereading the same line',
    'You drift halfway through a paragraph',
    'You read slowly because you are afraid of missing something',
    'You finish chapters but forget the main idea',
    'You want to read more books without shallow skimming',
    'You want a realistic reading routine you can actually keep',
];

const faqs = [
    {
        q: 'Is this a speed-reading trick?',
        a: 'No. ReaderFlex teaches comprehension-first speed. The goal is not to skim recklessly. The goal is to build better reading rhythm, reduce unnecessary rereading, and check understanding as you train.',
    },
    {
        q: 'Do I need ReaderFlex to use the companion kit?',
        a: 'No. You can use the kit with any book, article, or document. ReaderFlex simply gives you a cleaner way to practice guided rhythm, controlled speed, and comprehension checks.',
    },
    {
        q: 'Will this help me remember everything I read?',
        a: 'No method can honestly promise that. The kit gives you a practical retention system so you can remember more of what matters.',
    },
    {
        q: 'Is this medical advice?',
        a: 'No. The kit and ebook are educational. ReaderFlex is not presented as a treatment for ADHD, dyslexia, vision problems, or any medical or learning condition.',
    },
    {
        q: 'How long does the reset take?',
        a: 'The routine is designed around 15 minutes per day for 7 days.',
    },
];

const methodCards = [
    {
        icon: <MenuBookIcon sx={{ fontSize: 40, color: cyan }} />,
        title: 'Stop fighting the page',
        copy: 'Most readers slow down because their eyes keep jumping backward, losing place, or waiting for every word to be spoken internally.',
    },
    {
        icon: <VisibilityIcon sx={{ fontSize: 40, color: cyan }} />,
        title: 'Give your attention a path',
        copy: 'The Guide Dot creates motion and rhythm, giving your focus something to follow instead of forcing stillness.',
    },
    {
        icon: <PsychologyIcon sx={{ fontSize: 40, color: cyan }} />,
        title: 'Train speed with comprehension',
        copy: 'ReaderFlex is not about reckless skimming. It is about controlled speed, better pacing, and remembering more of what matters.',
    },
];

function EbookHeroVisual() {
    const lineData = [
        { w: 88, delay: 0 },
        { w: 72, delay: 0.1 },
        { w: 94, delay: 0.2 },
        { w: 65, delay: 0.3 },
        { w: 82, delay: 0.4 },
        { w: 90, delay: 0.5 },
        { w: 58, delay: 0.6 },
        { w: 76, delay: 0.7 },
    ];
    return (
        <Box
            sx={{
                position: 'absolute',
                right: { xs: '5%', md: '8%' },
                top: { xs: '55%', md: '50%' },
                transform: 'translateY(-50%)',
                width: { xs: 280, sm: 340, md: 420 },
                height: { xs: 240, sm: 300, md: 380 },
                display: { xs: 'none', sm: 'block' },
                zIndex: 1,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #0A1120 0%, #0E1A2E 100%)',
                    border: `1px solid ${alpha(cyan, 0.2)}`,
                    boxShadow: `0 0 60px ${alpha(cyan, 0.08)}, inset 0 0 40px ${alpha('#000', 0.5)}`,
                    p: { xs: 2.5, md: 3.5 },
                    overflow: 'hidden',
                }}
            >
                <Typography
                    sx={{
                        color: alpha(cyan, 0.6),
                        fontSize: '0.65rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        mb: 2,
                        animation: `${fadeUp} 0.5s ease both`,
                    }}
                >
                    Read Faster Without Forgetting Everything
                </Typography>
                {lineData.map((line, i) => (
                    <Box
                        key={i}
                        sx={{
                            height: 3,
                            mb: { xs: 2.5, md: 3 },
                            borderRadius: 2,
                            background: `linear-gradient(90deg, ${alpha('#fff', 0.03)} 0%, ${alpha('#fff', 0.07)} 50%, ${alpha('#fff', 0.01)} 100%)`,
                            width: `${line.w}%`,
                            animation: `${fadeUp} 0.6s ease ${line.delay}s both`,
                        }}
                    />
                ))}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '30%',
                        left: 0,
                        right: 0,
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${alpha(cyan, 0.2)}, transparent)`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '30%',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: cyan,
                        animation: `${guideDotMove} 2.5s ease-in-out infinite alternate, ${glowPulse} 1.5s ease-in-out infinite`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        right: -20,
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(green, 0.06)} 0%, transparent 70%)`,
                        pointerEvents: 'none',
                    }}
                />
            </Box>
        </Box>
    );
}

function EbookLanding() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [faqExpanded, setFaqExpanded] = useState(false);
    const formRef = useRef(null);
    const [showStickyCta, setShowStickyCta] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setShowStickyCta(window.scrollY > window.innerHeight * 0.8);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToForm = () => {
        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !email.includes('@')) {
            setError('Please enter a valid email address.');
            return;
        }

        setSubmitting(true);

        try {
            const params = new URLSearchParams(window.location.search);
            const response = await api.post('/ebook/opt-in', {
                email,
                firstName,
                source: params.get('utm_source') || 'direct',
                campaign: params.get('utm_campaign') || '7_day_reading_reset',
            });

            navigate('/ebook/thank-you');
        } catch (err) {
            setError(err.response?.data?.error || 'Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
    };

    const webPageJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Free 7-Day Reading Reset Companion Kit',
        description: 'Download the free companion kit for Read Faster Without Forgetting Everything. Practice controlled speed, reduce rereading, improve focus, and remember more of what you read.',
        url: 'https://readerflex.com/ebook',
        publisher: {
            '@type': 'Organization',
            name: 'ReaderFlex',
            url: 'https://readerflex.com',
        },
    };

    return (
        <>
            <Helmet>
                <title>Free 7-Day Reading Reset Companion Kit | ReaderFlex</title>
                <meta name="description" content="Download the free companion kit for Read Faster Without Forgetting Everything. Practice controlled speed, reduce rereading, improve focus, and remember more of what you read." />
                <link rel="canonical" href="https://readerflex.com/ebook" />
                <meta property="og:title" content="Free 7-Day Reading Reset Companion Kit" />
                <meta property="og:description" content="A practical companion kit from ReaderFlex to help you reduce rereading, build controlled speed, check comprehension, and remember more of what you read." />
                <meta property="og:url" content="https://readerflex.com/ebook" />
                <meta property="og:image" content="https://readerflex.com/images/readerflex-ebook-companion-kit-og.png" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Free 7-Day Reading Reset Companion Kit" />
                <meta name="twitter:description" content="Download the ReaderFlex companion kit and practice comprehension-first speed with the 7-Day Reading Reset." />
                <meta name="twitter:image" content="https://readerflex.com/images/readerflex-ebook-companion-kit-og.png" />
                <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
                <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
            </Helmet>

            <Box sx={{ backgroundColor: darkBg, color: '#fff' }}>
                {/* 1. HERO */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: { xs: '85vh', md: '92vh' },
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        background: `radial-gradient(ellipse at 30% 40%, ${alpha('#0A1A2E', 0.8)} 0%, ${darkBg} 70%)`,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            opacity: 0.4,
                            backgroundImage: `radial-gradient(circle at 20% 30%, ${alpha(cyan, 0.06)} 0%, transparent 40%), radial-gradient(circle at 80% 70%, ${alpha(green, 0.04)} 0%, transparent 40%)`,
                            zIndex: 0,
                        }}
                    />
                    <EbookHeroVisual />

                    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                        <Box sx={{ maxWidth: { xs: '100%', md: '55%' } }}>
                            <Typography
                                variant="overline"
                                sx={{ color: cyan, letterSpacing: '0.12em', fontSize: '0.75rem', display: 'block', mb: 2 }}
                            >
                                Companion Kit for Read Faster Without Forgetting Everything
                            </Typography>
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
                                    lineHeight: 1.15,
                                    letterSpacing: '-0.02em',
                                    mb: 3,
                                    color: '#fff',
                                }}
                            >
                                Get the Free 7-Day Reading{' '}
                                <Box component="span" sx={{ color: cyan }}>Reset</Box>{' '}
                                Companion Kit
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.2rem' },
                                    lineHeight: 1.6,
                                    color: alpha('#fff', 0.7),
                                    fontWeight: 400,
                                    mb: 4,
                                    maxWidth: '90%',
                                }}
                            >
                                A practical companion kit for Read Faster Without Forgetting Everything. Use it to reduce rereading, build controlled speed, check comprehension, and remember more of what you read.
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                <Button
                                    onClick={scrollToForm}
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        backgroundColor: cyan,
                                        color: '#000',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        boxShadow: `0 0 24px ${alpha(cyan, 0.4)}`,
                                        '&:hover': {
                                            backgroundColor: alpha(cyan, 0.85),
                                            boxShadow: `0 0 32px ${alpha(cyan, 0.6)}`,
                                        },
                                    }}
                                >
                                    Download the Free Kit
                                </Button>
                                <Button
                                    component={RouterLink}
                                    to="/blog"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderColor: alpha('#fff', 0.3),
                                        color: '#fff',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: cyan,
                                            color: cyan,
                                            backgroundColor: alpha(cyan, 0.05),
                                        },
                                    }}
                                >
                                    Preview What's Inside
                                </Button>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha('#fff', 0.35), fontSize: '0.85rem', fontStyle: 'italic' }}
                            >
                                No hype. No reckless skimming. Just practical reading drills built around comprehension-first speed.
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                {/* 2. WHO THIS IS FOR */}
                <Box sx={{ py: { xs: 6, md: 10 } }}>
                    <Container maxWidth="md">
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            This is for you if reading feels harder than it should.
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                color: alpha('#fff', 0.55),
                                fontWeight: 400,
                                textAlign: 'center',
                                mb: 5,
                            }}
                        >
                            You may not need another productivity hack. You may need a better reading system.
                        </Typography>
                        <Grid container spacing={2}>
                            {audienceBullets.map((bullet, i) => (
                                <Grid item xs={12} sm={6} key={i}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                            backgroundColor: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: 2,
                                            p: 2.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: cyan,
                                                mt: 0.7,
                                                flexShrink: 0,
                                                boxShadow: `0 0 8px ${alpha(cyan, 0.6)}`,
                                            }}
                                        />
                                        <Typography
                                            sx={{ color: alpha('#fff', 0.8), fontSize: '0.95rem', lineHeight: 1.5 }}
                                        >
                                            {bullet}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {/* 3. WHAT THE KIT INCLUDES */}
                <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: alpha('#000', 0.3) }}>
                    <Container maxWidth="lg">
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 2,
                                textAlign: 'center',
                            }}
                        >
                            What's inside the 7-Day Reading Reset companion kit
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                color: alpha('#fff', 0.5),
                                fontWeight: 400,
                                textAlign: 'center',
                                mb: { xs: 4, md: 6 },
                            }}
                        >
                            Six practical worksheets and trackers you can print or fill digitally.
                        </Typography>
                        <Grid container spacing={3}>
                            {kitItems.map((item, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Box
                                        sx={{
                                            backgroundColor: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: 3,
                                            p: 3,
                                            height: '100%',
                                            transition: 'all 0.3s ease',
                                            animation: `${fadeUp} 0.5s ease ${i * 0.08}s both`,
                                            '&:hover': {
                                                borderColor: alpha(cyan, 0.35),
                                                boxShadow: `0 8px 32px ${alpha(cyan, 0.1)}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ mb: 2 }}>{item.icon}</Box>
                                        <Typography
                                            variant="h6"
                                            sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', mb: 1.5, lineHeight: 1.3 }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: alpha('#fff', 0.6), fontSize: '0.875rem', lineHeight: 1.6 }}
                                        >
                                            {item.desc}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Box sx={{ textAlign: 'center', mt: 5 }}>
                            <Button
                                onClick={scrollToForm}
                                variant="contained"
                                size="large"
                                sx={{
                                    backgroundColor: cyan,
                                    color: '#000',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    boxShadow: `0 0 20px ${alpha(cyan, 0.3)}`,
                                    '&:hover': {
                                        backgroundColor: alpha(cyan, 0.85),
                                        boxShadow: `0 0 28px ${alpha(cyan, 0.5)}`,
                                    },
                                }}
                            >
                                Get the Full Free Kit
                            </Button>
                        </Box>
                    </Container>
                </Box>

                {/* 4. SAMPLE EXERCISE PREVIEW */}
                <Box sx={{ py: { xs: 6, md: 10 } }}>
                    <Container maxWidth="md">
                        <Typography
                            variant="overline"
                            sx={{ color: alpha(cyan, 0.6), letterSpacing: '0.1em', fontSize: '0.75rem', display: 'block', mb: 2, textAlign: 'center' }}
                        >
                            Preview
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            The No-Backtracking Drill
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: alpha('#fff', 0.65),
                                fontSize: '1.05rem',
                                lineHeight: 1.7,
                                mb: 4,
                                textAlign: 'center',
                            }}
                        >
                            One of the fastest ways to expose the rereading loop is to practice forward-only reading with a short, low-pressure passage.
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: cardBg,
                                border: `1px solid ${cardBorder}`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                            }}
                        >
                            {[
                                'Choose a 200 to 400 word passage.',
                                'Use a finger, cursor, pen, or the ReaderFlex Guide Dot.',
                                'Read forward without jumping back.',
                                'When finished, write one sentence summarizing the main idea.',
                                'Check whether your comprehension held without constant rereading.',
                            ].map((step, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        py: 1.5,
                                        borderBottom: i < 4 ? `1px solid ${alpha('#fff', 0.06)}` : 'none',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            border: `1px solid ${alpha(cyan, 0.3)}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: cyan,
                                            fontSize: '0.85rem',
                                            fontWeight: 700,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {i + 1}
                                    </Box>
                                    <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.95rem', lineHeight: 1.5 }}>
                                        {step}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha('#fff', 0.5), fontSize: '0.9rem', mb: 2 }}
                            >
                                The full companion kit includes the printable Rereading Loop Tracker.
                            </Typography>
                            <Button
                                onClick={scrollToForm}
                                variant="contained"
                                size="large"
                                sx={{
                                    backgroundColor: cyan,
                                    color: '#000',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    boxShadow: `0 0 20px ${alpha(cyan, 0.3)}`,
                                    '&:hover': {
                                        backgroundColor: alpha(cyan, 0.85),
                                        boxShadow: `0 0 28px ${alpha(cyan, 0.5)}`,
                                    },
                                }}
                            >
                                Get the Full Free Kit
                            </Button>
                        </Box>
                    </Container>
                </Box>

                {/* 5. READERFLEX BRIDGE */}
                <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: alpha('#000', 0.3) }}>
                    <Container maxWidth="lg">
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 2,
                                textAlign: 'center',
                            }}
                        >
                            Practice the method inside ReaderFlex
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                color: alpha('#fff', 0.55),
                                fontWeight: 400,
                                textAlign: 'center',
                                mb: { xs: 4, md: 6 },
                                maxWidth: '600px',
                                mx: 'auto',
                            }}
                        >
                            The companion kit shows you what to practice. ReaderFlex gives you a cleaner place to practice it.
                        </Typography>
                        <Grid container spacing={3} sx={{ mb: 5 }}>
                            {methodCards.map((card, i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <Box
                                        sx={{
                                            backgroundColor: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: 3,
                                            p: 4,
                                            height: '100%',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                borderColor: alpha(cyan, 0.35),
                                                boxShadow: `0 8px 32px ${alpha(cyan, 0.1)}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ mb: 2 }}>{card.icon}</Box>
                                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem', mb: 2, lineHeight: 1.3 }}>
                                            {card.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), fontSize: '0.9rem', lineHeight: 1.65 }}>
                                            {card.copy}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                variant="body1"
                                sx={{ color: alpha('#fff', 0.65), fontSize: '1.05rem', lineHeight: 1.7, mb: 4, maxWidth: '560px', mx: 'auto' }}
                            >
                                ReaderFlex is not magic. It is a training tool for readers who want rhythm, focus, and retention.
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/register"
                                variant="outlined"
                                size="large"
                                sx={{
                                    borderColor: cyan,
                                    color: cyan,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    '&:hover': {
                                        backgroundColor: alpha(cyan, 0.08),
                                        borderColor: cyan,
                                    },
                                }}
                            >
                                Try a Guided ReaderFlex Session
                            </Button>
                        </Box>
                    </Container>
                </Box>

                {/* 6. EMAIL OPT-IN FORM */}
                <Box ref={formRef} sx={{ py: { xs: 6, md: 10 }, scrollMarginTop: 80 }}>
                    <Container maxWidth="sm">
                        <Box
                            sx={{
                                textAlign: 'center',
                                backgroundColor: cardBg,
                                border: `1px solid ${alpha(cyan, 0.2)}`,
                                borderRadius: 4,
                                p: { xs: 4, md: 5 },
                                boxShadow: `0 0 60px ${alpha(cyan, 0.06)}`,
                            }}
                        >
                            <Typography
                                variant="h4"
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.1rem' },
                                    color: '#fff',
                                    letterSpacing: '-0.02em',
                                    mb: 3,
                                    lineHeight: 1.3,
                                }}
                            >
                                Download the free 7-Day Reading Reset companion kit
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: alpha('#fff', 0.65),
                                    fontSize: '1rem',
                                    lineHeight: 1.7,
                                    mb: 4,
                                }}
                            >
                                Enter your email and we'll send you the complete companion kit, including the worksheets, trackers, and 15-minute reading routine.
                            </Typography>
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Email address"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: alpha('#fff', 0.15) },
                                            '&:hover fieldset': { borderColor: alpha(cyan, 0.3) },
                                            '&.Mui-focused fieldset': { borderColor: cyan },
                                        },
                                        '& .MuiInputLabel-root': { color: alpha('#fff', 0.5) },
                                        '& .MuiInputLabel-root.Mui-focused': { color: cyan },
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="First name (optional)"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: alpha('#fff', 0.15) },
                                            '&:hover fieldset': { borderColor: alpha(cyan, 0.3) },
                                            '&.Mui-focused fieldset': { borderColor: cyan },
                                        },
                                        '& .MuiInputLabel-root': { color: alpha('#fff', 0.5) },
                                        '& .MuiInputLabel-root.Mui-focused': { color: cyan },
                                    }}
                                />
                                {error && (
                                    <Alert severity="error" sx={{ backgroundColor: alpha('#f44336', 0.1), color: '#ff6b6b' }}>
                                        {error}
                                    </Alert>
                                )}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={submitting}
                                    sx={{
                                        backgroundColor: cyan,
                                        color: '#000',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                        py: 1.5,
                                        borderRadius: 2,
                                        boxShadow: `0 0 24px ${alpha(cyan, 0.3)}`,
                                        '&:hover': {
                                            backgroundColor: alpha(cyan, 0.85),
                                            boxShadow: `0 0 32px ${alpha(cyan, 0.5)}`,
                                        },
                                        '&:disabled': {
                                            backgroundColor: alpha(cyan, 0.3),
                                        },
                                    }}
                                >
                                    {submitting ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Send Me the Free Kit'}
                                </Button>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha('#fff', 0.35), fontSize: '0.8rem', mt: 2 }}
                            >
                                Free download. No spam. Unsubscribe anytime.
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: alpha('#fff', 0.25), fontSize: '0.7rem', display: 'block', mt: 1, lineHeight: 1.5 }}
                            >
                                By submitting this form, you agree to receive ReaderFlex emails related to reading improvement, ReaderFlex resources, and product updates. You can unsubscribe anytime.
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                {/* 7. FAQ */}
                <Box sx={{ py: { xs: 6, md: 10 } }}>
                    <Container maxWidth="md">
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: { xs: 3, md: 5 },
                                textAlign: 'center',
                            }}
                        >
                            Frequently asked questions
                        </Typography>
                        {faqs.map((faq, i) => (
                            <Accordion
                                key={i}
                                expanded={faqExpanded === i}
                                onChange={() => setFaqExpanded(faqExpanded === i ? false : i)}
                                sx={{
                                    backgroundColor: cardBg,
                                    border: `1px solid ${cardBorder}`,
                                    borderRadius: '12px !important',
                                    mb: 2,
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': { borderColor: alpha(cyan, 0.3) },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: cyan }} />} sx={{ '&:hover': { backgroundColor: alpha(cyan, 0.03) } }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{faq.q}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.95rem', lineHeight: 1.7 }}>{faq.a}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Container>
                </Box>

                {/* 8. FINAL CTA */}
                <Box
                    sx={{
                        py: { xs: 8, md: 12 },
                        textAlign: 'center',
                        background: `radial-gradient(ellipse at center, ${alpha('#0A1A2E', 0.6)} 0%, ${darkBg} 70%)`,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.6rem', sm: '2.2rem', md: '2.8rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 3,
                                lineHeight: 1.2,
                            }}
                        >
                            Stop fighting the page. Train the habit.
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                color: alpha('#fff', 0.55),
                                fontWeight: 400,
                                lineHeight: 1.6,
                                mb: 5,
                                maxWidth: '540px',
                                mx: 'auto',
                            }}
                        >
                            Reading faster is not about becoming a machine. It is about building a better rhythm with your eyes, attention, and memory.
                        </Typography>
                        <Button
                            onClick={scrollToForm}
                            variant="contained"
                            size="large"
                            sx={{
                                backgroundColor: cyan,
                                color: '#000',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1.15rem',
                                px: 5,
                                py: 1.5,
                                borderRadius: 2,
                                boxShadow: `0 0 30px ${alpha(cyan, 0.4)}`,
                                '&:hover': {
                                    backgroundColor: alpha(cyan, 0.85),
                                    boxShadow: `0 0 40px ${alpha(cyan, 0.6)}`,
                                },
                            }}
                        >
                            Download the Free Companion Kit
                        </Button>
                    </Container>
                </Box>

                {/* DISCLAIMER FOOTER */}
                <Box sx={{ py: 4, backgroundColor: alpha('#000', 0.4) }}>
                    <Container maxWidth="md">
                        <Typography
                            variant="caption"
                            sx={{ color: alpha('#fff', 0.25), fontSize: '0.7rem', display: 'block', textAlign: 'center', lineHeight: 1.6 }}
                        >
                            ReaderFlex resources are educational and informational. They are not medical advice. ReaderFlex is not presented as a treatment for ADHD, dyslexia, vision problems, or any medical or learning condition. Readers with medical, vision, or learning concerns should consult qualified professionals.
                        </Typography>
                    </Container>
                </Box>

                {/* STICKY MOBILE CTA */}
                <Fade in={showStickyCta}>
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: alpha('#060B14', 0.95),
                            borderTop: `1px solid ${alpha(cyan, 0.2)}`,
                            p: 2,
                            zIndex: 1000,
                            backdropFilter: 'blur(10px)',
                            justifyContent: 'center',
                        }}
                    >
                        <Button
                            onClick={scrollToForm}
                            variant="contained"
                            fullWidth
                            sx={{
                                backgroundColor: cyan,
                                color: '#000',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                                py: 1.2,
                                borderRadius: 2,
                                boxShadow: `0 0 16px ${alpha(cyan, 0.3)}`,
                                '&:hover': { backgroundColor: alpha(cyan, 0.85) },
                            }}
                        >
                            Get the Free Kit
                        </Button>
                    </Box>
                </Fade>
            </Box>
        </>
    );
}

export default EbookLanding;
